import { useLocation, useNavigate } from "react-router-dom";
import { contactInfo } from "@/mocks/homeData";

export default function Footer() {
  const location = useLocation();
  const navigate = useNavigate();
  const isHomepage = location.pathname === "/";

  const handleNavClick = (href: string) => {
    if (href.startsWith("/#")) {
      const sectionId = href.replace("/#", "");
      if (isHomepage) {
        const el = document.getElementById(sectionId);
        if (el) {
          const navHeight = 80;
          const y = el.getBoundingClientRect().top + window.scrollY - navHeight;
          window.scrollTo({ top: y, behavior: "smooth" });
        }
      } else {
        navigate("/", { state: { scrollTo: sectionId } });
      }
    } else if (href.startsWith("/")) {
      navigate(href);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const quickLinks = [
    { label: "Home", href: "/#home" },
    { label: "About Us", href: "/about" },
    { label: "Capabilities", href: "/#capabilities" },
    { label: "Products", href: "/#products" },
    { label: "Quality", href: "/#quality" },
    { label: "Sustainability", href: "/#sustainability" },
    { label: "News", href: "/#news" },
    { label: "Contact", href: "/#contact" },
  ];

  return (
    <footer className="bg-primary-900 text-white">
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          <div className="lg:col-span-1">
            <div className="flex items-center gap-4 mb-5">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center p-1">
                <img
                  src="/images/logo.jpeg"
                  alt="Beaumonde Tex Logo"
                  className="w-full h-full object-contain"
                  style={{ aspectRatio: "1 / 1" }}
                />
              </div>
              <div>
                <div className="font-heading text-sm md:text-base font-semibold leading-tight">
               Beaumonde Tex Ltd
                </div>
              </div>
            </div>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Premium apparel manufacturing in Bangladesh. Delivering quality,
              compliance, and reliable supply chain performance to global
              brands.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={contactInfo.linkedin}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="LinkedIn"
              >
                <i className="ri-linkedin-fill text-white/80 text-sm"></i>
              </a>
              <a
                href={`https://wa.me/${contactInfo.whatsapp.replace("+", "")}`}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="WhatsApp"
              >
                <i className="ri-whatsapp-line text-white/80 text-sm"></i>
              </a>
              <a
                href={`mailto:${contactInfo.email}`}
                className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors"
                aria-label="Email"
              >
                <i className="ri-mail-line text-white/80 text-sm"></i>
              </a>
            </div>
          </div>

          <div>
            <h4 className="text-xs font-heading font-semibold text-white/90 uppercase tracking-wider mb-5">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.slice(0, 4).map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-heading font-semibold text-white/90 uppercase tracking-wider mb-5">
              More
            </h4>
            <ul className="space-y-3">
              {quickLinks.slice(4).map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link.href);
                    }}
                    className="text-sm text-white/60 hover:text-white transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-heading font-semibold text-white/90 uppercase tracking-wider mb-5">
              Contact
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-map-pin-line text-white/40 text-sm mt-0.5"></i>
                <p className="text-sm text-white/60 leading-relaxed">
                  {contactInfo.officeAddress}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-phone-line text-white/40 text-sm"></i>
                <a
                  href="tel:+8801674283025"
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  +880 1674-283025
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-global-line text-white/40 text-sm"></i>
                <a
                  href="https://www.beaumondetex.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  www.beaumondetex.com
                </a>
              </div>
              <div className="flex items-center gap-3">
                <i className="ri-mail-line text-white/40 text-sm"></i>
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="text-sm text-white/60 hover:text-white transition-colors"
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-white/40">
            &copy; {new Date().getFullYear()} BA Fashionwear Ltd. All rights
            reserved.
          </p>
          <p className="text-xs text-white/40">
            A facility of{" "}
            <strong className="text-white/60">Beaumonde Tex Ltd.</strong>
          </p>
        </div>
      </div>
    </footer>
  );
}
